import { Inject } from '@odinjs/odin';
import { FetchAPI, FormAPI, ModalButton, ModalService, ThemeTonalRange, ToastService } from '@philips-internal/oswald-ui-components';
import { ModalButtonType, ToastLevel } from '@philips-internal/oswald-ui-constants';
import Controller, { OnBeforeCancel, OnBeforeFetch, OnChange, OnClose, OnSubmit } from '@philips-internal/oswald-ui-core/lib/client';
import { ChangeEvent, FetchEvent, SubmitEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { HttpResponse } from '@philips-internal/oswald-ui-core/lib/services/http-service/http-service-types';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';

import { ApplicationRoute, ModulesRoute } from '../../constants';
import { Endpoint } from '../../constants/endpoints';
import showSuccessToast from '../../utils/show-toast';
import { approvalForwardModelID } from '../approvals/approvals-item-controller';
import BaseController from '../BaseController';
import { DialogType, ModalType, SimpleDialog, SimpleDialogParams } from '../dialog-helper/dialog-helper';

import { FormBody, normalizeForwardBody } from './approval-forward-business';

interface RoleValue {
  id: number | string
  name: string
}

export interface Form {
  physicalPerson: PhysicalPerson
  role: Role | undefined
  observation: string
}

interface Role {
  id: number | string,
  name: string
}

interface PhysicalPerson {
  physicalPersonId: number | string,
  name: string
}

interface ApprovalProcessAvailableVO {
  amount: number,
  message: string
}

@Controller({ fragment: 'approval-forward-modal', component: 'layout-forward' })
export default class ApprovalForwardModelController extends BaseController {

  constructor() {
    super();
    this.completeSubmit.bind(this);
    this.onSubmitForwardForm.bind(this);
  }

  @Inject('forward-form')
    form: FormAPI;

  @OnBeforeCancel({ target: 'forward-form' })
  onFormBeforeCancel(): void {

    const record = this.form.getRecord();
    this.form.setRecord(record);
    this.form.setDisabled(true);

    const dialogParams: SimpleDialogParams = {
      closeable: false,
      descriptionDialog: 'approval.discard.changes',
      dialogType: DialogType.ACTION,
      modalType: ModalType.DIALOG,
      onClickCancel: () => { this.cancelDiscard();},
      onClickOk: () => { this.discard();},
      titleDialog: 'approvals.action.forward',
      titleOnCancel: 'tws.form.cancel',
      titleOnOk: 'approval.discard',
      theme: ThemeTonalRange.VERY_DARK,
    };
    SimpleDialog.openDialog(dialogParams);
  }

  @OnSubmit('forward-form')
  onSubmitForwardForm(event: SubmitEvent): void {
    const { id, processId } = this.historyService.getCurrentRouteParams() as Record<string, undefined>;
    const formData = event.record as unknown as Form;

    const body = normalizeForwardBody(formData, id, processId);
    this.context.set({ approvalBody: body });

    this.form.setLoading(true);

    HttpService.post<ApprovalProcessAvailableVO>(Endpoint.FORWARD_VERIFICATION, {}, { body })
      .then(response => this.handlerProcessValidation(response))
      .then(userConfirmation => this.forwardProcess(userConfirmation))
      .catch(error => this.handlerError(error))
      .finally(() => this.completeSubmit());
  }

  @OnChange('field-role')
  onChangeFieldRole(event: ChangeEvent<RoleValue>): void {
    event.value && this.context.set({ roleId: event.value.id });
  }

  @OnBeforeFetch('fetch-physical-person')
  onBeforeFetchPhysicalPerson(event: FetchEvent): void {
    const fetch = event.target as FetchAPI;
    const roleId = this.context.get('roleId');

    roleId && fetch.setEndpoint(Endpoint.GET_USER_WITH_ROLEID.replace(':roleId', String(roleId)));
    !roleId && fetch.setEndpoint(Endpoint.GET_USER_WITHOUT_ROLEID);
  }

  @OnClose(['approval-forward-modal', 'modal-forward'])
  onCloseModal(): void {
    ModalService.close(approvalForwardModelID);
  }

  handlerProcessValidation(response: HttpResponse<ApprovalProcessAvailableVO>): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const body = response.body;
      if (!body) {
        return reject();
      }
      if (body?.amount > 0) {
        const buttons: ModalButton[] = [];

        buttons.push({
          action: () => resolve(true),
          title: 'approvals.action.forward',
          type: ModalButtonType.SUCCESS,
        });

        buttons.push({
          action: () => resolve(false),
          title: 'approval.cancel',
          type: ModalButtonType.CANCEL,
        });

        ModalService.openDialog({
          dockButtons: buttons,
          dialogDescription: response.body?.message,
          id: 'approval-forward-confirm-modal',
          focusOnOpen: true,
          onClose: () => resolve(false),
        });
      } else {
        return resolve(true);
      }
    });
  }

  forwardProcess(userConfirmation: boolean): Promise<any> {
    if (!userConfirmation) {
      return Promise.resolve();
    }

    const formContent = this.context.get('approvalBody') as FormBody;
    return HttpService.post<void>(Endpoint.FORWARD_PENDING_PROCESS, {}, { body: formContent });
  }

  handlerError(error: any): void {
    this.context.set({ hasErrors: true, error });
    ToastService.open({
      id: 'approval-forward-toast',
      dismissible: false,
      message: 'Error.',
      level: ToastLevel.ERROR,
      timeout: 5000,
    });
  }

  completeSubmit(): void {
    const hasErrorrs = this.context.get('hasErrors') as boolean | undefined;
    this.form.setLoading(false);
    if (!hasErrorrs) {
      applicationContext.redirect(`${ModulesRoute.APPROVALS}${ApplicationRoute.APPROVAL_LIST}`);
      showSuccessToast('forward-complete-toast', 'pending.approval.action.completed.successfully');
    }

    this.context.set({ hasErrorrs: false, error: undefined });
  }

  cancelDiscard(): void {
    this.form.setDisabled(false);
    this.form.setEditing(true);
  }

  discard(): void {
    applicationContext.redirect(
      `${ModulesRoute.APPROVALS}${ApplicationRoute.APPROVAL_LIST}`
    );
  }
}
